import React, { useEffect, useState, useRef } from 'react';

import { loadScript } from '@crehana/utils';

import { TextField, EmailField, PrimaryButton } from '@crehana/ui';

import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import { primaryLight } from '@crehana/ui/dist/styles/colors';

import { useTranslation } from '@crehana/i18n';

import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';
import SecurePayment from 'Jsx/CheckoutV4/shared/SecurePayment';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import useCustomPayment from 'Jsx/CheckoutV4/hooks/useCustomPayment';

import { URLS } from 'Jsx/CheckoutV4/common/constants';

import LegalCheckbox from 'Jsx/CheckoutV4/components/LegalCheckbox';
import NewsLetterCheckbox from 'Jsx/CheckoutV4/components/NewsLetterCheckbox';

import CardLogos from 'Jsx/CheckoutV4/components/CardLogos';
import { REGEX_EMAIL } from 'Jsx/Shared/regex';

import getCountryPrefix from 'Jsx/Utils/getCountryPrefix';

import {
  getCardErrorMessage,
  getDNIText,
  getDNIDescription,
  getDniMaxLength,
  getEmptyDniErrorMessage,
  getInvalidDniErrorMessage,
  isValidDNI,
} from './utils';

export interface DLocalFormProps {
  itemId?: number | null;
  itemType?: string | null;
}

const DLocalForm: React.FC<DLocalFormProps> = ({ itemId }) => {
  const { t } = useTranslation();
  const countryPrefix = getCountryPrefix();
  const [isDlocalLoaded, setIsDlocalLoaded] = useState(false);
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardHolderError, setCardHolderError] = useState({
    message: '',
    error: false,
  });
  const [dniError, setDniError] = useState({
    message: '',
    error: false,
  });
  const [email, setEmail] = useState('');
  const [dni, setDni] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [cardInfoError, setCardInfoError] = useState(0);
  const [checkedTerms, setCheckedTerms] = useState(true);
  const [checkedNewsLetterTerms, setCheckedNewsLetterTerms] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { selectedPaymentMethod } = useCheckoutContext();

  const fields =
    useRef<{
      create?: (value: string, options: any) => any;
    } | null>();
  const dlocal =
    useRef<{
      createToken?: (node: unknown, options: any) => any;
      fields: any;
    } | null>();
  const cardNumberField =
    useRef<{
      mount: (node: HTMLElement | null) => void;
    }>();
  const cvvField =
    useRef<{
      mount: (node: HTMLElement | null) => void;
    }>();
  const expirationField =
    useRef<{
      mount: (node: HTMLElement | null) => void;
    }>();
  const { executePayment } = useCustomPayment();
  const { me } = useMe();

  useEffect(() => {
    if (!isDlocalLoaded) {
      loadScript({ src: URLS.dlocal }).then(response => {
        if (response) {
          setIsDlocalLoaded(true);
        }
      });
    }
  }, [isDlocalLoaded]);

  const createSmartFields = () => {
    const style = {
      base: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      focus: {
        borderColor: primaryLight,
        border: '1px solid red',
      },
    };

    cardNumberField.current = fields.current?.create?.('pan', {
      style,
      placeholder: 'Número de tarjeta',
    });
    expirationField.current = fields.current?.create?.('expiration', {
      style,
      placeholder: 'MM/AA',
    });
    cvvField.current = fields.current?.create?.('cvv', { style });

    if (cardNumberField.current) {
      cardNumberField.current.mount(document.getElementById('pan-field'));
    }

    if (expirationField.current) {
      expirationField.current.mount(
        document.getElementById('expiration-field'),
      );
    }

    if (cvvField.current) {
      cvvField.current.mount(document.getElementById('cvv-field'));
    }
  };

  useEffect(() => {
    if (isDlocalLoaded) {
      dlocal.current = window.dlocal(window.DLOCAL_API_KEY);

      fields.current = dlocal?.current?.fields({
        locale: 'en',
        country: countryPrefix?.toUpperCase(),
      });

      createSmartFields();
    }

    return () => {
      dlocal.current = null;
      fields.current = null;
    };
  }, [countryPrefix, isDlocalLoaded]);

  const checksum = (s: string) => {
    let hash = 0;
    const strlen = s.length;

    if (strlen === 0) {
      return hash;
    }

    for (let i = 0; i < strlen; i += 1) {
      const c = s.charCodeAt(i);

      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + c;
      // eslint-disable-next-line no-bitwise
      hash &= hash; // Convert to 32bit integer
    }

    return Math.abs(hash);
  };

  const generateDeviceId = () => {
    const nav = window.navigator;
    const { screen } = window;
    let deviceId: string = nav.mimeTypes.length.toString();

    Object.values(navigator.mimeTypes).forEach(p => {
      deviceId += p.type;
    });
    deviceId += nav.userAgent.replace(/\D+/g, '');
    deviceId += nav.plugins.length.toString();

    Object.values(navigator.plugins).forEach(p => {
      deviceId += p.filename;
    });

    deviceId += screen.height.toString() || '';
    deviceId += screen.width.toString() || '';
    deviceId += screen.pixelDepth.toString() || '';

    return checksum(deviceId);
  };

  const payment = async (dlocalToken: string) => {
    setProcessing(true);
    const deviceId = generateDeviceId().toString();

    try {
      if (selectedPaymentMethod && itemId && deviceId) {
        const { data } = await executePayment({
          deviceId,
          fullName: cardHolderName,
          email: me?.email || email,
          document: dni,
          dlocalToken,
          stripeSafeCard: checkedTerms,
          checkedNewsLetterTerms,
        });

        if (data?.payment) {
          const { success, redirectUrl, messages } = data.payment;

          if (success && redirectUrl) {
            window.location.href = redirectUrl;
          }

          if (!success) {
            // @ts-expect-error
            const { message: errorMessage } = messages[0];

            setPaymentError(errorMessage);
          }
        }

        setProcessing(false);
      }
    } catch (error) {
      setProcessing(false);
      setPaymentError(t('CATCH_MESSAGE'));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const { token: dlocalToken } = await dlocal?.current?.createToken?.(
        cardNumberField.current,
        {
          name: cardHolderName,
        },
      );

      if (dlocalToken) {
        payment(dlocalToken);
      }
    } catch ({ error }) {
      setCardInfoError(0);
    }
  };

  const onBlurName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      setCardHolderError({
        message: 'Nombre completo obligatorio',
        error: true,
      });
    }

    if (event.target.value) {
      setCardHolderError({
        message: '',
        error: false,
      });
    }
  };

  const handleChangeHolderName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCardHolderName(event.target.value);
  };

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleChangeDNI = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDni(event.target.value);
  };

  const isValidEmail = (emailValue: string) => {
    if (!emailValue) {
      return false;
    }

    return emailValue.length && REGEX_EMAIL.test(emailValue);
  };

  const onBlurDni = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      const dniEmptyErrorMessage = getEmptyDniErrorMessage(countryPrefix);

      setDniError({ message: dniEmptyErrorMessage, error: true });
    }

    if (event.target.value && !isValidDNI(event.target.value, countryPrefix)) {
      const dniInvalidErrorMessage = getInvalidDniErrorMessage(countryPrefix);

      setDniError({ message: dniInvalidErrorMessage, error: true });
    }

    if (
      event.target.value.length >= 7 &&
      isValidDNI(event.target.value, countryPrefix)
    ) {
      setDniError({ message: '', error: false });
    }
  };

  const isCompleteForm = () => {
    if (me && me?.email) {
      return (
        cardHolderName.length &&
        isValidEmail(me?.email) &&
        isValidDNI(dni, countryPrefix) &&
        checkedTerms
      );
    }

    return (
      cardHolderName.length &&
      isValidEmail(email) &&
      isValidDNI(dni, countryPrefix) &&
      checkedTerms
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="pb-40"
      method="post"
      id="payment-form"
      style={{ maxWidth: 399 }}
    >
      <TextField
        id="card-holder"
        name="card-holder"
        className="mb-24"
        label="Nombre completo"
        maxLength={50}
        full
        onBlur={onBlurName}
        error={cardHolderError.error}
        errorText={cardHolderError.message}
        onChange={handleChangeHolderName}
        value={cardHolderName}
        check={Boolean(!cardHolderError.error && cardHolderName)}
      />
      <TextField
        id="dni"
        name="dni"
        description={getDNIDescription(countryPrefix)}
        className="mb-24"
        label={getDNIText(countryPrefix)}
        maxLength={getDniMaxLength(countryPrefix)}
        full
        onBlur={onBlurDni}
        error={dniError.error}
        errorText={dniError.message}
        onChange={handleChangeDNI}
        value={dni}
        check={!dniError.error && isValidDNI(dni, countryPrefix)}
        data-testid="document-number"
      />
      {!me && (
        <EmailField
          id="email"
          className="mb-24"
          type="email"
          name="email"
          label="Correo electrónico"
          maxLength={50}
          full
          onChange={handleChangeEmail}
          value={email}
        />
      )}
      <div className="mb-24">
        <div className="flex justify-between mb-8">
          <label htmlFor="pan-field" className="self-end font-subtitle3">
            Número de tarjeta
          </label>
          <CardLogos />
        </div>
        <div
          id="pan-field"
          className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
        />
        {cardInfoError === 1 && (
          <span className="font-caption text-red-main">
            {t(getCardErrorMessage(cardInfoError))}
          </span>
        )}
      </div>
      <div className="flex w-full gap-24 mb-24">
        <div className="w-full mr-24">
          <label htmlFor="expiration-field" className="font-subtitle3">
            Vencimiento (MM/AA)
          </label>
          <div
            id="expiration-field"
            className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
          />
          {cardInfoError === 2 && (
            <span className="font-caption text-red-main">
              {getCardErrorMessage(cardInfoError)}
            </span>
          )}
        </div>
        <div className="w-full">
          <label htmlFor="cvv-field" className="font-subtitle3">
            Código de seguridad
          </label>
          <div
            id="cvv-field"
            className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
          />
          {cardInfoError === 3 && (
            <span className="font-caption text-red-main">
              {getCardErrorMessage(cardInfoError)}
            </span>
          )}
        </div>
      </div>
      <LegalCheckbox
        className="mb-20"
        checkedTerms={checkedTerms}
        setCheckedTerms={setCheckedTerms}
      />
      <NewsLetterCheckbox
        className="mb-40"
        checkedNewsLetterTerms={checkedNewsLetterTerms}
        setCheckedNewsLetterTerms={setCheckedNewsLetterTerms}
      />

      {paymentError && (
        <p className="mt-8 mb-8 tw-font-semibold text-center font-legals text-red-main">
          {paymentError}
        </p>
      )}
      <div className="w-full mt-24 sm:max-w-360">
        <PrimaryButton
          type="submit"
          isAccent
          size="l"
          full
          disabled={!isCompleteForm() || processing}
          className="mb-40"
          label="Comprar ahora"
        />
      </div>
      <SecurePayment />
      {processing && <PaymentsLoader type="purchaseOnline" />}
    </form>
  );
};

export default DLocalForm;
